import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Card, CardContent } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { formatDateShort, formatEpisodeNumber } from '../utils/format'
import Tag from './Tag'
import { colors } from '../styles/theme'

const EpisodeCard = ({ episode, index = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      whileHover={{ y: -8, transition: { duration: 0.2 } }}
    >
      <Link to={`/episodes/${episode.slug}`} className="block group">
        <Card
          sx={{
            backgroundColor: colors.bgInput,
            color: colors.textPrimary,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            border: `1px solid ${colors.borderAccent}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: 'var(--shadow-card-hover)',
              borderColor: colors.borderAccentHover,
            },
          }}
        >
          {/* Gradient Header with Episode Number and Title */}
          <div className="relative h-40 overflow-hidden bg-gradient-to-br from-purple/20 via-slate/40 to-cyan/20 px-6 py-4 flex flex-col justify-between">
            {/* Top: Episode number and Play icon */}
            <div className="flex items-start justify-between relative z-10">
              <div className="text-5xl font-bold font-mono text-cyan/40">
                {episode.number.toString().padStart(2, '0')}
              </div>

              {/* Play icon */}
              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-12 h-12 bg-cyan rounded-full flex items-center justify-center" style={{ boxShadow: 'var(--shadow-accent-glow-lg)' }}>
                  <PlayArrowIcon sx={{ fontSize: 24, color: colors.textOnAccent }} />
                </div>
              </div>
            </div>

            {/* Bottom: Title */}
            <h3 className="text-sm font-medium text-gray-300/90 line-clamp-2 leading-snug relative z-10">
              {episode.title}
            </h3>

            {/* Decorative elements */}
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-purple/10 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-cyan/10 rounded-full blur-xl"></div>
          </div>

          <CardContent sx={{ flexGrow: 1, p: 3 }}>
            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-4">
              {episode.tags.slice(0, 3).map((tag) => (
                <Tag key={tag} tag={tag} size="small" />
              ))}
            </div>

            {/* The Prompt - Main Focus */}
            <div className="mb-4 p-3 bg-slate/40 rounded-lg border-l-4 border-purple">
              <div className="text-xs font-mono text-purple mb-1 uppercase tracking-wider">
                Episode Prompt
              </div>
              <blockquote className="text-base font-medium text-off-white leading-snug line-clamp-3">
                "{episode.prompt}"
              </blockquote>
            </div>

            {/* Summary */}
            <p className="text-sm text-gray-400 mb-4 line-clamp-2">{episode.summary}</p>

            {/* Meta info */}
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>{formatDateShort(episode.date)}</span>
              <span className="font-mono">{episode.duration}</span>
            </div>
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  )
}

export default EpisodeCard
